import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Plus, Trash2, Edit2, Pin, Clock, Radio, CircleDot, Square,
  ChevronDown, ChevronUp, Loader2, AlertTriangle, Save, X
} from 'lucide-react';

function adminFetch(url, opts = {}) {
  const token = sessionStorage.getItem('admin_token');
  const headers = { ...(opts.headers || {}) };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return fetch(url, { ...opts, credentials: 'include', headers });
}

const STATUS_OPTIONS = [
  { value: 'upcoming', label: 'Upcoming', color: 'text-blue-400', bg: 'bg-blue-500/10' },
  { value: 'live', label: 'LIVE', color: 'text-red-400', bg: 'bg-red-500/10' },
  { value: 'ht', label: 'HT', color: 'text-yellow-400', bg: 'bg-yellow-500/10' },
  { value: 'ft', label: 'FT', color: 'text-white/50', bg: 'bg-white/5' },
  { value: 'postponed', label: 'PPD', color: 'text-orange-400', bg: 'bg-orange-500/10' },
];

const EVENT_TYPES = [
  { value: 'goal', label: 'Goal', icon: '⚽' },
  { value: 'red_card', label: 'Red Card', icon: '🟥' },
  { value: 'yellow_card', label: 'Yellow Card', icon: '🟨' },
  { value: 'penalty', label: 'Penalty Goal', icon: '⚽(P)' },
  { value: 'own_goal', label: 'Own Goal', icon: '⚽(OG)' },
];

export default function LiveTickerTab() {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [form, setForm] = useState({
    home_team: '', away_team: '', competition: '', status: 'upcoming',
    home_score: 0, away_score: 0, minute: '', pinned: false,
  });
  const [eventForm, setEventForm] = useState({ event_type: 'goal', team: 'home', minute: '', player: '' });
  const [saving, setSaving] = useState(false);

  const fetchMatches = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminFetch('/api/admin/live-ticker');
      if (res.ok) {
        const data = await res.json();
        setMatches(data.matches || []);
      } else if (res.status === 401) {
        toast.error('Admin session expired.');
      }
    } catch { toast.error('Failed to load ticker matches'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchMatches(); }, [fetchMatches]);

  const resetForm = () => {
    setForm({ home_team: '', away_team: '', competition: '', status: 'upcoming', home_score: 0, away_score: 0, minute: '', pinned: false });
    setEditingId(null);
    setShowAddForm(false);
  };

  const handleSave = async () => {
    if (!form.home_team.trim() || !form.away_team.trim()) {
      toast.error('Both teams are required');
      return;
    }
    setSaving(true);
    try {
      const url = editingId ? `/api/admin/live-ticker/${editingId}` : '/api/admin/live-ticker';
      const method = editingId ? 'PUT' : 'POST';
      const res = await adminFetch(url, {
        method, headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        toast.success(editingId ? 'Match updated' : 'Match added to ticker');
        resetForm();
        fetchMatches();
      } else {
        const d = await res.json().catch(() => ({}));
        toast.error(d.detail || 'Failed to save');
      }
    } catch { toast.error('Network error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this match from the ticker?')) return;
    try {
      const res = await adminFetch(`/api/admin/live-ticker/${id}`, { method: 'DELETE' });
      if (res.ok) { toast.success('Match removed'); fetchMatches(); }
    } catch { toast.error('Failed to delete'); }
  };

  const handleQuickUpdate = async (id, field, value) => {
    try {
      const res = await adminFetch(`/api/admin/live-ticker/${id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [field]: value }),
      });
      if (res.ok) fetchMatches();
    } catch { toast.error('Update failed'); }
  };

  const handleAddEvent = async (matchId) => {
    if (!eventForm.minute.trim()) { toast.error('Minute is required'); return; }
    try {
      const res = await adminFetch(`/api/admin/live-ticker/${matchId}/event`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(eventForm),
      });
      if (res.ok) {
        toast.success('Event added');
        setEventForm({ event_type: 'goal', team: 'home', minute: '', player: '' });
        fetchMatches();
      }
    } catch { toast.error('Failed to add event'); }
  };

  const handleRemoveLastEvent = async (matchId) => {
    try {
      const res = await adminFetch(`/api/admin/live-ticker/${matchId}/event`, { method: 'DELETE' });
      if (res.ok) { toast.success('Last event removed'); fetchMatches(); }
      else {
        const d = await res.json().catch(() => ({}));
        toast.error(d.detail || 'Failed');
      }
    } catch { toast.error('Failed'); }
  };

  const startEdit = (match) => {
    setForm({
      home_team: match.home_team, away_team: match.away_team,
      competition: match.competition || '', status: match.status,
      home_score: match.home_score, away_score: match.away_score,
      minute: match.minute || '', pinned: match.pinned || false,
    });
    setEditingId(match.id);
    setShowAddForm(true);
  };

  const getStatusBadge = (status) => {
    const opt = STATUS_OPTIONS.find(o => o.value === status) || STATUS_OPTIONS[0];
    return <span className={`px-2 py-0.5 rounded text-xs font-bold ${opt.color} ${opt.bg}`}>{opt.label}</span>;
  };

  return (
    <div className="space-y-6" data-testid="live-ticker-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <Radio className="w-5 h-5 text-red-500" />
            Live Ticker Manager
          </h3>
          <p className="text-xs text-white/40 mt-1">
            {matches.length} match{matches.length !== 1 ? 'es' : ''} in ticker
          </p>
        </div>
        <button
          onClick={() => { resetForm(); setShowAddForm(true); }}
          data-testid="add-ticker-match-btn"
          className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-sm font-medium transition-colors"
        >
          <Plus className="w-4 h-4" /> Add Match
        </button>
      </div>

      {/* Add/Edit Form */}
      {showAddForm && (
        <div className="bg-[#1a1a1a] rounded-xl p-4 border border-white/10 space-y-4" data-testid="ticker-match-form">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-white">{editingId ? 'Edit Match' : 'Add Match'}</h4>
            <button onClick={resetForm} className="text-white/40 hover:text-white"><X className="w-4 h-4" /></button>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-white/50 mb-1 block">Home Team *</label>
              <input data-testid="ticker-home-team" value={form.home_team} onChange={e => setForm(f => ({ ...f, home_team: e.target.value }))}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" placeholder="e.g. Enyimba" />
            </div>
            <div>
              <label className="text-xs text-white/50 mb-1 block">Away Team *</label>
              <input data-testid="ticker-away-team" value={form.away_team} onChange={e => setForm(f => ({ ...f, away_team: e.target.value }))}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" placeholder="e.g. Rivers Utd" />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-white/50 mb-1 block">Competition</label>
              <input value={form.competition} onChange={e => setForm(f => ({ ...f, competition: e.target.value }))}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" placeholder="e.g. NPFL" />
            </div>
            <div>
              <label className="text-xs text-white/50 mb-1 block">Status</label>
              <select data-testid="ticker-status" value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white">
                {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-white/50 mb-1 block">Minute</label>
              <input value={form.minute} onChange={e => setForm(f => ({ ...f, minute: e.target.value }))}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" placeholder="e.g. 45'" />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-white/50 mb-1 block">Home Score</label>
              <input type="number" min="0" value={form.home_score} onChange={e => setForm(f => ({ ...f, home_score: parseInt(e.target.value) || 0 }))}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" />
            </div>
            <div>
              <label className="text-xs text-white/50 mb-1 block">Away Score</label>
              <input type="number" min="0" value={form.away_score} onChange={e => setForm(f => ({ ...f, away_score: parseInt(e.target.value) || 0 }))}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 cursor-pointer py-2">
                <input type="checkbox" checked={form.pinned} onChange={e => setForm(f => ({ ...f, pinned: e.target.checked }))}
                  className="rounded border-white/20 bg-white/5" />
                <Pin className="w-3.5 h-3.5 text-yellow-400" />
                <span className="text-xs text-white/60">Pin to top</span>
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button onClick={resetForm} className="px-4 py-2 text-sm text-white/50 hover:text-white">Cancel</button>
            <button onClick={handleSave} disabled={saving} data-testid="ticker-save-btn"
              className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white rounded-lg text-sm font-medium">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {editingId ? 'Update' : 'Add'}
            </button>
          </div>
        </div>
      )}

      {/* Matches List */}
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-white/30" /></div>
      ) : matches.length === 0 ? (
        <div className="text-center py-12 text-white/30">
          <Radio className="w-8 h-8 mx-auto mb-2 opacity-30" />
          <p>No matches in ticker. Add one to get started.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {matches.map(match => (
            <div key={match.id} className="bg-[#1a1a1a] rounded-xl border border-white/5 overflow-hidden" data-testid={`ticker-match-${match.id}`}>
              {/* Match Row */}
              <div className="flex items-center gap-3 px-4 py-3">
                {/* Pin indicator */}
                {match.pinned && <Pin className="w-3.5 h-3.5 text-yellow-400 shrink-0" />}

                {/* Status */}
                {getStatusBadge(match.status)}

                {/* Teams & Score */}
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <span className="text-white font-medium text-sm truncate">{match.home_team}</span>
                  <span className="text-white font-bold tabular-nums text-sm shrink-0">
                    {match.home_score} — {match.away_score}
                  </span>
                  <span className="text-white font-medium text-sm truncate">{match.away_team}</span>
                  {match.minute && (
                    <span className="text-white/40 text-xs shrink-0">| {match.minute}</span>
                  )}
                </div>

                {/* Competition */}
                {match.competition && (
                  <span className="text-white/30 text-xs shrink-0 hidden sm:block">{match.competition}</span>
                )}

                {/* Events icons */}
                <div className="flex items-center gap-0.5 shrink-0">
                  {(match.events || []).map((ev, i) => (
                    <span key={i} className="text-xs" title={`${ev.event_type} ${ev.minute} ${ev.player}`}>
                      {ev.event_type === 'goal' || ev.event_type === 'penalty' ? '⚽' :
                       ev.event_type === 'own_goal' ? '⚽' :
                       ev.event_type === 'red_card' ? '🟥' :
                       ev.event_type === 'yellow_card' ? '🟨' : ''}
                    </span>
                  ))}
                </div>

                {/* Quick status buttons */}
                <div className="flex items-center gap-1 shrink-0">
                  {match.status !== 'live' && (
                    <button onClick={() => handleQuickUpdate(match.id, 'status', 'live')} title="Set LIVE"
                      className="p-1.5 rounded-lg hover:bg-red-500/20 text-red-400 transition-colors">
                      <Radio className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {match.status === 'live' && (
                    <button onClick={() => handleQuickUpdate(match.id, 'status', 'ht')} title="Set Half Time"
                      className="p-1.5 rounded-lg hover:bg-yellow-500/20 text-yellow-400 transition-colors">
                      <Clock className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {match.status === 'ht' && (
                    <button onClick={() => handleQuickUpdate(match.id, 'status', 'live')} title="Resume 2nd Half"
                      className="p-1.5 rounded-lg hover:bg-red-500/20 text-red-400 transition-colors">
                      <Radio className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {(match.status === 'live' || match.status === 'ht') && (
                    <button onClick={() => handleQuickUpdate(match.id, 'status', 'ft')} title="Set Full Time"
                      className="p-1.5 rounded-lg hover:bg-white/10 text-white/50 transition-colors">
                      <Square className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => setExpandedId(expandedId === match.id ? null : match.id)}
                    className="p-1.5 rounded-lg hover:bg-white/10 text-white/40 transition-colors">
                    {expandedId === match.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                  <button onClick={() => startEdit(match)}
                    className="p-1.5 rounded-lg hover:bg-white/10 text-white/40 transition-colors">
                    <Edit2 className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => handleDelete(match.id)}
                    className="p-1.5 rounded-lg hover:bg-red-500/20 text-red-400/50 hover:text-red-400 transition-colors">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

              {/* Expanded: Events & Quick Actions */}
              {expandedId === match.id && (
                <div className="border-t border-white/5 px-4 py-3 space-y-3 bg-[#151515]">
                  {/* Quick score update */}
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-white/40 w-16">Score:</span>
                    <div className="flex items-center gap-2">
                      <button onClick={() => handleQuickUpdate(match.id, 'home_score', Math.max(0, match.home_score - 1))}
                        className="w-6 h-6 rounded bg-white/5 text-white/50 hover:bg-white/10 text-xs">-</button>
                      <span className="text-white font-bold text-sm tabular-nums w-8 text-center">{match.home_score}</span>
                      <button onClick={() => handleQuickUpdate(match.id, 'home_score', match.home_score + 1)}
                        className="w-6 h-6 rounded bg-white/5 text-white/50 hover:bg-white/10 text-xs">+</button>
                      <span className="text-white/30 mx-1">—</span>
                      <button onClick={() => handleQuickUpdate(match.id, 'away_score', Math.max(0, match.away_score - 1))}
                        className="w-6 h-6 rounded bg-white/5 text-white/50 hover:bg-white/10 text-xs">-</button>
                      <span className="text-white font-bold text-sm tabular-nums w-8 text-center">{match.away_score}</span>
                      <button onClick={() => handleQuickUpdate(match.id, 'away_score', match.away_score + 1)}
                        className="w-6 h-6 rounded bg-white/5 text-white/50 hover:bg-white/10 text-xs">+</button>
                    </div>
                  </div>

                  {/* Quick minute update */}
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-white/40 w-16">Minute:</span>
                    <div className="flex items-center gap-1">
                      {["1'","15'","30'","45'","45+2'","HT","46'","60'","75'","90'","90+3'","FT"].map(m => (
                        <button key={m} onClick={() => handleQuickUpdate(match.id, 'minute', m)}
                          className={`px-2 py-1 rounded text-xs transition-colors ${match.minute === m ? 'bg-emerald-600 text-white' : 'bg-white/5 text-white/40 hover:bg-white/10'}`}>
                          {m}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Events list */}
                  {match.events?.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-white/40">Events ({match.events.length})</span>
                        <button onClick={() => handleRemoveLastEvent(match.id)}
                          className="text-xs text-red-400/60 hover:text-red-400">Undo last</button>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {match.events.map((ev, i) => (
                          <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-white/5 rounded text-xs text-white/60">
                            {ev.event_type === 'goal' ? '⚽' : ev.event_type === 'red_card' ? '🟥' :
                             ev.event_type === 'yellow_card' ? '🟨' : ev.event_type === 'penalty' ? '⚽(P)' :
                             ev.event_type === 'own_goal' ? '⚽(OG)' : ''}
                            <span>{ev.minute}</span>
                            {ev.player && <span className="text-white/40">{ev.player}</span>}
                            <span className={`text-[10px] ${ev.team === 'home' ? 'text-blue-400' : 'text-red-400'}`}>
                              ({ev.team === 'home' ? match.home_team.substring(0, 3).toUpperCase() : match.away_team.substring(0, 3).toUpperCase()})
                            </span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Add event form */}
                  <div className="flex items-center gap-2 pt-1">
                    <select value={eventForm.event_type} onChange={e => setEventForm(f => ({ ...f, event_type: e.target.value }))}
                      className="bg-white/5 border border-white/10 rounded px-2 py-1.5 text-xs text-white">
                      {EVENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.icon} {t.label}</option>)}
                    </select>
                    <select value={eventForm.team} onChange={e => setEventForm(f => ({ ...f, team: e.target.value }))}
                      className="bg-white/5 border border-white/10 rounded px-2 py-1.5 text-xs text-white">
                      <option value="home">{match.home_team}</option>
                      <option value="away">{match.away_team}</option>
                    </select>
                    <input value={eventForm.minute} onChange={e => setEventForm(f => ({ ...f, minute: e.target.value }))}
                      className="w-16 bg-white/5 border border-white/10 rounded px-2 py-1.5 text-xs text-white" placeholder="Min" />
                    <input value={eventForm.player} onChange={e => setEventForm(f => ({ ...f, player: e.target.value }))}
                      className="flex-1 bg-white/5 border border-white/10 rounded px-2 py-1.5 text-xs text-white" placeholder="Player (optional)" />
                    <button onClick={() => handleAddEvent(match.id)} data-testid="add-event-btn"
                      className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded text-xs font-medium">
                      Add
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
